// Runtime-agnostic bounding-box content classification, shared by client and server.
//
// Every bounding box is classified by the OCR step into one of these content types.
// The classification then drives the grading / generation input strategy for a workstep
// (replacing the previously hard-coded per-taskType strategies) and the frontend display.

/** What a single bounding box visually contains. Produced by the OCR classifier. */
export type BoundingBoxContentType = 'image' | 'text' | 'both';

/** The input strategy for a workstep (grading, MS-generation, GS-generation). */
export type InputStrategy = 'ocrText' | 'image' | 'both';

export const BOUNDING_BOX_CONTENT_TYPES: readonly BoundingBoxContentType[] = ['image', 'text', 'both'] as const;

/**
 * Legacy bounding boxes (created before content-type classification existed) have no
 * `contentType`. We treat a missing classification as `'both'` so grading/generation keep
 * using every available modality and never silently drop data for old exams.
 */
export const LEGACY_CONTENT_TYPE: BoundingBoxContentType = 'both';

export function normalizeContentType(value: unknown): BoundingBoxContentType {
  return (BOUNDING_BOX_CONTENT_TYPES as readonly string[]).includes(value as string)
    ? (value as BoundingBoxContentType)
    : LEGACY_CONTENT_TYPE;
}

/**
 * Derive the input strategy for a workstep from the content types of the bounding boxes it
 * operates on:
 *   - only `text` boxes            -> `'ocrText'`
 *   - only `image` boxes           -> `'image'`
 *   - any `both`, or a text/image mix -> `'both'`
 *   - no boxes at all              -> `'ocrText'` (nothing visual to send; fall back to text)
 *
 * Boxes with a missing/unknown `contentType` are treated as `'both'` (see LEGACY_CONTENT_TYPE).
 */
export function deriveInputStrategy(contentTypes: Array<BoundingBoxContentType | null | undefined>): InputStrategy {
  const normalized = (contentTypes ?? []).map(normalizeContentType);
  if (normalized.length === 0) return 'ocrText';

  const hasImage = normalized.includes('image');
  const hasText = normalized.includes('text');
  const hasBoth = normalized.includes('both');

  if (hasBoth) return 'both';
  if (hasImage && hasText) return 'both';
  if (hasImage) return 'image';
  // only text remains
  return 'ocrText';
}

/**
 * Frontend display: whether to show the OCR/transcribed-text UI for a box.
 * An `image` box has no transcribable text, so its text UI is hidden and only the cropped
 * image is shown. `text` and `both` boxes keep their text UI.
 */
export function shouldShowOcrText(contentType: BoundingBoxContentType | null | undefined): boolean {
  return normalizeContentType(contentType) !== 'image';
}

/**
 * Aggregate the content type of a *section* (a set of boxes — e.g. a task's description,
 * master-solution, or grading-scheme boxes) into a single display content type:
 *   - only text -> 'text'   - only image -> 'image'   - any mix / any both -> 'both'   - none -> 'text'
 * (Same buckets as deriveInputStrategy, but reported as a content type with 'text' instead of 'ocrText'.)
 */
export function deriveContentType(
  contentTypes: Array<BoundingBoxContentType | null | undefined>,
): BoundingBoxContentType {
  const strategy = deriveInputStrategy(contentTypes);
  return strategy === 'ocrText' ? 'text' : strategy;
}

export function deriveContentTypeFromBoxes(
  boxes: Array<{ contentType?: BoundingBoxContentType | null }> | null | undefined,
): BoundingBoxContentType {
  return deriveContentType((boxes ?? []).map(box => box?.contentType));
}

/**
 * The image ids of boxes that carry a displayable image — i.e. 'image' or 'both' (and legacy boxes,
 * treated as 'both'); never 'text'. Used everywhere we want "the images, with text coming from the
 * concatenated content instead": text boxes contribute their text via the section's content, image
 * boxes contribute their cropped image. Pairs with the OCR guard that keeps 'image' boxes text-free.
 */
export function imageBoxImageIds(
  boxes: Array<{ contentType?: BoundingBoxContentType | null; imageId?: string | null }> | null | undefined,
): string[] {
  return (boxes ?? [])
    .filter(box => normalizeContentType(box?.contentType) !== 'text')
    .map(box => String(box?.imageId ?? '').trim())
    .filter(id => id.length > 0);
}

/**
 * After a solution's text is replaced (e.g. an MS/GS that was originally SCANNED and then refined or
 * regenerated), its bounding boxes still carry the stale OCR text of the old scan. The new text now
 * lives in the solution's content/criteria field, so the boxes must keep only their IMAGES: drop
 * pure-'text' boxes entirely, and downgrade image-bearing boxes ('both' / legacy) to 'image' with
 * their now-superseded text cleared. All other box fields (geometry, imageId, …) are preserved.
 * Accepts raw docs or hydrated entities (unwraps `.doc`); returns plain box objects ready to persist.
 */
export function stripTextFromBoxes(boxes: any): any[] {
  return (Array.isArray(boxes) ? boxes : [])
    .map((box: any) => box?.doc ?? box)
    .filter((box: any) => normalizeContentType(box?.contentType) !== 'text')
    .map((box: any) => ({ ...box, contentType: 'image' as BoundingBoxContentType, ocrExtractedText: '' }));
}
